import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Card, CardContent, Skeleton, Chip, IconButton, Button, CircularProgress, Alert } from '@mui/material';
import { Inventory, ShoppingCart, People, TrendingUp, Refresh as RefreshIcon, LocalOffer, Settings, LocalShipping } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { adminService, shippingService } from '../../services/api';
import { firestore as db } from '../../config/firebase';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { COLORS } from '../../theme/colors';

// Lightweight card component
const StatCard = ({ title, value, subtitle, icon: Icon, color, loading }) => {
  return (
    <Card elevation={0} sx={(theme)=>({
      height:'100%',
      border:'1px solid '+theme.palette.divider,
      borderRadius:2,
      background: theme.palette.background.paper,
      cursor:'default'
    })}>
      <CardContent sx={{ py:2.25 }}>
        <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight:600, letterSpacing:.5 }}>{title}</Typography>
            {loading ? <Skeleton variant="text" width={60} height={34} /> : (
              <Typography variant="h5" sx={{ fontWeight:700, mt:.25 }}>{typeof value === 'number' ? value.toLocaleString() : value}</Typography>
            )}
            {subtitle && <Typography variant="caption" color="text.secondary" sx={{ mt:.5, display:'block' }}>{subtitle}</Typography>}
          </Box>
          <Box sx={{ width:46, height:46, borderRadius:1.75, bgcolor: color+'22', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <Icon sx={{ fontSize:26, color }} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

// Recent Orders list (simplified)
const RecentOrders = ({ orders = [], loading }) => {
  const list = Array.isArray(orders) ? orders : [];

  const formatCreatedAt = (ts) => {
    if (!ts) return '';
    // Firestore Timestamp (client) has toDate; Admin SDK serialized via JSON yields {_seconds,_nanoseconds}
    try {
      if (ts.toDate) return ts.toDate().toLocaleDateString();
      const seconds = ts.seconds ?? ts._seconds; // support both shapes
      if (typeof seconds === 'number') return new Date(seconds * 1000).toLocaleDateString();
    } catch {}
    return '';
  };

  if (loading) return (
    <Box>{Array.from({length:3}).map((_,i)=><Skeleton key={i} variant="rectangular" height={58} sx={{ mb:1 }} />)}</Box>
  );
  if (!list.length) return <Typography variant="body2" color="text.secondary">No recent orders</Typography>;
  return (
    <Box>
      {list.map(o=> {
        const dateStr = formatCreatedAt(o.createdAt);
        return (
          <Card key={o.id} variant="outlined" sx={{ mb:1 }}>
            <CardContent sx={{ py:1.25 }}>
              <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <Box>
                  <Typography variant="subtitle2">#{o.orderNumber || o.id}</Typography>
                  {dateStr && <Typography variant="caption" color="text.secondary">{dateStr}</Typography>}
                </Box>
                <Box sx={{ textAlign:'right' }}>
                  <Typography variant="subtitle2">₹{o.pricing?.total || 0}</Typography>
                  <Chip size="small" label={o.status || 'pending'} color={(o.status === 'confirmed') ? 'success':'warning'} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        );
      })}
    </Box>
  );
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({ totalProducts:0, totalOrders:0, totalUsers:0, totalRevenue:0, shippingBase:0, freeShippingThreshold:null });
  const [recentOrders, setRecentOrders] = useState([]);
  const [dataCache, setDataCache] = useState({ stats:null, statsTimestamp:null, orders:null, ordersTimestamp:null });
  const CACHE = 5*60*1000;
  const cacheValid = (t) => t && (Date.now()-t < CACHE);

  const loadStats = async () => {
    if (cacheValid(dataCache.statsTimestamp)) { setStats(dataCache.stats); setStatsLoading(false); return; }
    try {
      setStatsLoading(true);
      const metricsRef = doc(db,'metrics','counts');
      const snap = await getDoc(metricsRef);
      if (snap.exists()) {
        const m = snap.data();
        const s = { totalProducts: m.totalProducts||0, totalOrders: m.totalOrders||0, totalUsers: m.totalUsers||0, totalRevenue: m.totalRevenue||0 };
        setStats(prev=>({...prev,...s}));
        setDataCache(p=>({...p, stats:{...stats,...s}, statsTimestamp:Date.now()}));
      }
      // shipping settings
      try {
        const res = await shippingService.getSettings();
        const data = res.data||{};
        const lowest = Array.isArray(data.options)&&data.options.length ? data.options.reduce((min,o)=>o.price<min?o.price:min,data.options[0].price):0;
        setStats(prev=>({...prev, shippingBase:lowest, freeShippingThreshold:data.freeShippingThreshold??null }));
      } catch {}
    } catch (e){
      console.error('Stats load error', e);
    } finally { setStatsLoading(false); }
  };

  const loadOrders = async () => {
    if (cacheValid(dataCache.ordersTimestamp)) { setRecentOrders(dataCache.orders||[]); return; }
    try {
      setOrdersLoading(true);
      const res = await adminService.getRecentOrders(5);
      const list = Array.isArray(res.data)? res.data : [];
      setRecentOrders(list);
      setDataCache(p=>({...p, orders:list, ordersTimestamp:Date.now()}));
    } catch { setRecentOrders([]); } finally { setOrdersLoading(false); }
  };

  const loadAll = async () => {
    setLoading(true); setError(null);
    await Promise.all([loadStats(), loadOrders()]).catch(e=> setError('Failed to load dashboard'));
    setLoading(false);
  };

  const refresh = async () => { setDataCache({ stats:null, statsTimestamp:null, orders:null, ordersTimestamp:null }); await loadAll(); toast.success('Refreshed'); };

  useEffect(()=>{ loadAll(); const ref = doc(db,'metrics','counts'); const unsub = onSnapshot(ref,s=>{ if(s.exists()){ const m=s.data(); setStats(prev=>({...prev,totalProducts:m.totalProducts||0,totalOrders:m.totalOrders||0,totalUsers:m.totalUsers||0,totalRevenue:m.totalRevenue||0})); }}); return ()=>unsub(); },[]);

  const cards = [
    { title:'Products', value:stats.totalProducts, subtitle:'Active SKUs', icon:Inventory, color:COLORS.info?.main || COLORS.primary.main },
    { title:'Orders (24h)', value:stats.totalOrders, subtitle:'+0 vs. prev.', icon:ShoppingCart, color:COLORS.success?.main || COLORS.primary.main },
    { title:'Users', value:stats.totalUsers, subtitle:'+0 today', icon:People, color:COLORS.warning?.main || COLORS.accent?.main || COLORS.primary.main },
    { title:'Revenue', value:`₹${stats.totalRevenue.toLocaleString()}`, subtitle:'Today', icon:TrendingUp, color:COLORS.error?.main || COLORS.secondary?.main || COLORS.primary.main }
  ];

  if (loading && !cacheValid(dataCache.statsTimestamp)) return <Box sx={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'50vh' }}><CircularProgress /></Box>;

  return (
      <Box>
        {error && <Alert severity="error" sx={{ mb:2 }}>{error}</Alert>}
        <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb:2 }}>
          <Typography variant="h5" sx={{ fontWeight:700 }}>Admin Snapshot</Typography>
          <IconButton size="small" onClick={refresh} disabled={loading}><RefreshIcon fontSize="small" /></IconButton>
        </Box>
        <Grid container spacing={2} sx={{ mb:3 }}>
          {cards.map((c,i)=>(
            <Grid item xs={12} sm={6} md={3} key={i}>
              <StatCard {...c} loading={statsLoading} />
            </Grid>
          ))}
        </Grid>
        <Grid container spacing={2} sx={{ mb:3 }}>
          <Grid item xs={12} md={4}>
            <Card variant="outlined" sx={{ height:'100%' }}>
              <CardContent sx={{ pb:1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight:600, mb:1.5 }}>Shipping</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb:1 }}>Lowest Option: ₹{stats.shippingBase}</Typography>
                {stats.freeShippingThreshold!=null && <Chip size="small" color="success" label={`Free ≥ ₹${stats.freeShippingThreshold}`} />}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
            <Card variant="outlined" sx={{ height:420, display:'flex', flexDirection:'column' }}>
              <CardContent sx={{ flexGrow:1, pb:1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight:600, mb:2 }}>Recent Orders</Typography>
                <RecentOrders orders={recentOrders} loading={ordersLoading} />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={5}>
            <Card variant="outlined" sx={{ height:420 }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight:600, mb:2 }}>System Status</Typography>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight:500 }}>Database</Typography>
                  <Box sx={{ display:'flex', alignItems:'center', mb:1 }}><Box sx={{ width:10, height:10, borderRadius:'50%', bgcolor:'success.main', mr:1 }} /><Typography variant="caption">Connected</Typography></Box>
                  <Typography variant="body2" sx={{ fontWeight:500 }}>Realtime Metrics</Typography>
                  <Box sx={{ display:'flex', alignItems:'center', mb:1 }}><Box sx={{ width:10, height:10, borderRadius:'50%', bgcolor:'success.main', mr:1 }} /><Typography variant="caption">Active</Typography></Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
  </Box>
  );
};

export default AdminDashboard;
        